import queryEncodeArray from '@/utils/Encoders/queryEncodeArray'
import queryEncodeObject from '@/utils/Encoders/queryEncodeObject'

/**
 * Abstract class that contains all the basic logic regarding encoding and decoding.
 * Must overwrite the encode and decode functions
 */
export default class AbstractEncoder {
  protected _data: any

  protected _decoder: any

  protected _encoder: any

  protected _default: any

  protected _filter: any

  get data() {
    return this._data
  }

  set data(data) {
    this._data = AbstractEncoder.Observe(data, this.encode.bind(this))
    this.encode()
  }

  /**
   * @description
   *  The class works by taking in initially checking if it is given any data. If not it will try to automatically
   *  if possible. Therefore if you manually want to start decoding the data from the query, then give it an empty
   *  object or string initially.
   * @param {any} data            - The data to wrap
   * @param {function} decoder    - The decoder used to decode the query string into usable data
   * @param {function} encoder    - Encodes the data into a string, if none is given it will try to do so automatically
   * @param {any} defaultVal      - When decoding fails it sets the data to this
   * @param {function} filter     - Allows you to filter out the data before it gets encoded.
   *                                The output of this function is passed to the encoder function
   */
  constructor({
    data = null,
    decoder = null,
    encoder = null,
    defaultVal = null,
    filter = (_) => _,
  } = {}) {
    // Don't try to use this class on its own!
    if (this.constructor === AbstractEncoder) {
      throw new Error("Abstract classes can't be instantiated.")
    }
    this._data = AbstractEncoder.Observe(data, this.encode.bind(this))
    this._decoder = decoder
    this._encoder = encoder
    this._default = defaultVal
    this._filter = filter
  }

  /**
   *  This is the basic encoder function that gets used to encode data into strings that can then be decoded again
   *  The data gets encoded in the following order, data => filter => encoder.
   *  If no encoder has been given it will try to figure out if it is an array or an object and encode appropriately.
   *  This is fine for simple objects or arrays but for more complicated use-cases a custom encoder function is
   *  recommended
   * @returns {*|string}
   * @private
   */
  public _encode() {
    if (this._encoder !== null && typeof this._encoder === 'function') {
      return this._encoder(this._filter(this.data), this)
    }
    if (this._data !== null && this._data !== undefined) {
      if (typeof this._data !== 'object') {
        return `${this._filter(this._data)}`
      }
      return this._data instanceof Array
        ? queryEncodeArray(this._filter(this._data))
        : queryEncodeObject(this._filter(this._data))
    }

    return ''
  }

  // This part should be implemented by extensions of this class
  encode() {
    throw new Error('Not Implemented')
  }

  /**
   * This method will try to decode the received input into usable data
   * If it fails it will either set it to the default value or just leave the data unchanged
   * @param {*} raw
   * @returns {*}
   * @private
   */
  _decode(raw) {
    const res = typeof this._decoder === 'function' ? this._decoder(raw, this) : raw

    // we first take the result that we might have gotten if no such result exist, we then use the default. If no
    // default has been specified, we don't change anything.
    return res || this._default || this._data
  }

  // This part should be implemented by extensions of this class
  decode() {
    throw new Error('Not Implemented')
  }

  /**
   * This adds an observer to the data and calls the method when changes occur.
   * It can listen for changes in objects and arrays, but does not support deep watch of objects.
   * @param {*} data    - Value to watch
   * @param method      - Method to call when changes occur
   * @returns {Proxy|*} - It will return a proxy if data is an array or an object otherwise it returns the data
   * @constructor
   */
  static Observe(data, method) {
    if (!(data instanceof Object)) return data
    return new Proxy(data, {
      set(oTarget, sKey, vValue) {
        // eslint-disable-next-line no-param-reassign
        oTarget[sKey] = vValue
        if (!(data instanceof Array && sKey === 'length')) {
          method()
        }
        return oTarget[sKey]
      },
      deleteProperty(oTarget, sKey) {
        const v = oTarget.removeItem(sKey)
        method()
        return v
      },
    })
  }
}
